import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../config';
import { PatternType } from '../../../../types/pattern';
import { patternStatus } from '../../../../constants/enum';
import { convertItemListToCamelCase } from '../../../../utils/pattern/itemListConverter';

export interface GetPatternsPaginatedResult {
  results: PatternType[];
  lastVisible: DocumentSnapshot | null;
  isLastPage: boolean;
}

/**
 * 読取パターン一覧をページネートで取得（会社IDでフィルタリング）
 */
export const getPatternsPaginated = async (
  resultLimit: number,
  lastVisible: DocumentSnapshot | null = null,
  companyId?: string,
  includeDeleted = false,
): Promise<GetPatternsPaginatedResult> => {
  const patternsRef = collection(db, 'patterns');

  const statusConstraints = includeDeleted ? [] : [where('status', '==', patternStatus.valid)];

  // 会社IDでフィルタリングしたクエリを作成（statusフィルタをFirestoreレベルで適用）
  let q = companyId
    ? query(
        patternsRef,
        where('company_id', '==', companyId),
        ...statusConstraints,
        orderBy('created_at', 'desc'),
        limit(resultLimit),
      )
    : query(patternsRef, ...statusConstraints, orderBy('created_at', 'desc'), limit(resultLimit));

  if (lastVisible) {
    q = companyId
      ? query(
          patternsRef,
          where('company_id', '==', companyId),
          ...statusConstraints,
          orderBy('created_at', 'desc'),
          startAfter(lastVisible),
          limit(resultLimit),
        )
      : query(
          patternsRef,
          ...statusConstraints,
          orderBy('created_at', 'desc'),
          startAfter(lastVisible),
          limit(resultLimit),
        );
  }

  const querySnapshot = await getDocs(q);

  const results: PatternType[] = [];

  querySnapshot.forEach(docSnapshot => {
    const data = docSnapshot.data();

    results.push({
      patternId: docSnapshot.id,
      pattern_name: data.pattern_name,
      document_name: data.document_name || '',
      description: data.description,
      remarks: data.remarks,
      file_type: data.file_type,
      item_count: data.item_count,
      item_list: convertItemListToCamelCase(data.item_list || []),
      is_public: data.is_public,
      status: data.status,
      sample_files: data.sample_files || [],
      gemini_model: data.gemini_model,
      ocr_engine: data.ocr_engine, // #3264
      is_classification_target: data.is_classification_target,
      share_scope: data.share_scope,
      shared_group_ids: data.shared_group_ids,
      shared_user_ids: data.shared_user_ids,
      owner_user_id: data.owner_user_id,
      has_master_mapping: data.has_master_mapping,
      has_sub_patterns: data.has_sub_patterns,
      sub_pattern_count: data.sub_pattern_count,
      company_id: data.company_id,
      is_proxy: data.is_proxy,
      created_at: data.created_at,
      updated_at: data.updated_at,
      created_by: data.created_by,
      updated_by: data.updated_by,
    });
  });

  const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
  const isLastPage = querySnapshot.docs.length < resultLimit;

  return {
    results,
    lastVisible: lastDoc,
    isLastPage,
  };
};

/**
 * 単一の読取パターンを取得
 */
export const getPattern = async (patternId: string): Promise<PatternType | null> => {
  const patternRef = doc(db, 'patterns', patternId);
  const patternDoc = await getDoc(patternRef);

  if (!patternDoc.exists()) {
    return null;
  }

  const data = patternDoc.data();

  // item_listをsnake_caseからcamelCaseに変換
  const itemListWithDefaults = convertItemListToCamelCase(data.item_list || []);

  return {
    patternId: patternDoc.id,
    pattern_name: data.pattern_name,
    document_name: data.document_name || '',
    description: data.description,
    remarks: data.remarks,
    file_type: data.file_type,
    item_count: data.item_count,
    item_list: itemListWithDefaults,
    result_data: data.result_data || [],
    is_public: data.is_public,
    status: data.status,
    sample_files: data.sample_files || [],
    original_sample_files: data.original_sample_files || [],
    gemini_model: data.gemini_model,
    ocr_engine: data.ocr_engine, // #3264
    prompt: data.prompt,
    use_custom_prompt: data.use_custom_prompt,
    is_classification_target: data.is_classification_target,
    has_master_mapping: data.has_master_mapping,
    has_sub_patterns: data.has_sub_patterns,
    sub_pattern_count: data.sub_pattern_count,
    export_settings: data.export_settings,
    search_settings: data.search_settings,
    preview_completed_at: data.preview_completed_at,
    company_id: data.company_id,
    is_proxy: data.is_proxy,
    // 共有範囲（#2804 / 担当者指定 #2963）
    share_scope: data.share_scope,
    shared_group_ids: data.shared_group_ids,
    shared_user_ids: data.shared_user_ids,
    owner_user_id: data.owner_user_id,
    created_at: data.created_at,
    updated_at: data.updated_at,
    created_by: data.created_by,
    updated_by: data.updated_by,
  };
};

/**
 * 読取パターンIDで読取パターンを取得（結果データ用）- 空の配列を返す
 */
export const getPatternByPatternId = async (): Promise<unknown[]> => {
  // 読取パターン機能では結果データは不要なので空配列を返す
  return [];
};

/**
 * 企業ごとの読取パターン数を取得
 * @param companyId - 企業ID
 * @returns 企業に紐づく有効な読取パターン数
 */
export const getPatternCountByCompanyId = async (companyId: string): Promise<number> => {
  const patternsRef = collection(db, 'patterns');
  const patternQuery = query(
    patternsRef,
    where('company_id', '==', companyId),
    where('status', '==', patternStatus.valid),
  );
  const patternSnapshot = await getDocs(patternQuery);

  return patternSnapshot.size;
};

/**
 * 企業に削除済みの読取パターンが存在するかを判定
 * @param companyId 企業ID
 * @returns 削除済みパターンが1件以上あればtrue
 */
export const hasDeletedPatternsByCompanyId = async (companyId: string): Promise<boolean> => {
  const patternsRef = collection(db, 'patterns');
  const patternQuery = query(
    patternsRef,
    where('company_id', '==', companyId),
    where('status', '==', patternStatus.deleted),
    limit(1),
  );
  const snapshot = await getDocs(patternQuery);
  return !snapshot.empty;
};

/**
 * 企業毎の読取パターンを一括取得（display_order順）
 * パターン数が少ない前提のため、ページネーションなしで全件取得する
 * @param includeDeleted - trueの場合、削除済み（status=99）も含めて取得する
 */
export const getAllPatternsByCompanyId = async (
  companyId: string,
  includeDeleted = false,
): Promise<PatternType[]> => {
  const patternsRef = collection(db, 'patterns');
  const q = query(patternsRef, where('company_id', '==', companyId));

  const querySnapshot = await getDocs(q);
  const results: PatternType[] = [];

  querySnapshot.forEach(docSnapshot => {
    const data = docSnapshot.data();

    // includeDeletedがfalseの場合はstatus === 1のもののみを含める
    if (!includeDeleted && data.status !== patternStatus.valid) return;

    results.push({
      patternId: docSnapshot.id,
      pattern_name: data.pattern_name,
      document_name: data.document_name || '',
      description: data.description,
      remarks: data.remarks,
      file_type: data.file_type,
      item_count: data.item_count,
      item_list: convertItemListToCamelCase(data.item_list || []),
      is_public: data.is_public,
      status: data.status,
      sample_files: data.sample_files || [],
      gemini_model: data.gemini_model,
      ocr_engine: data.ocr_engine, // #3264
      is_classification_target: data.is_classification_target,
      share_scope: data.share_scope,
      shared_group_ids: data.shared_group_ids,
      shared_user_ids: data.shared_user_ids,
      owner_user_id: data.owner_user_id,
      has_sub_patterns: data.has_sub_patterns,
      sub_pattern_count: data.sub_pattern_count,
      display_order: data.display_order,
      company_id: data.company_id,
      is_proxy: data.is_proxy,
      created_at: data.created_at,
      updated_at: data.updated_at,
      created_by: data.created_by,
      updated_by: data.updated_by,
    });
  });

  // display_order でソート（未設定のものは末尾に配置）
  results.sort((a, b) => {
    const orderA = a.display_order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.display_order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    // display_order が同じ場合は created_at 降順
    return (b.created_at?.seconds ?? 0) - (a.created_at?.seconds ?? 0);
  });

  return results;
};
